import { Box, Divider, Typography, useTheme } from '@mui/material';
import { generateClient, SelectionSet } from 'aws-amplify/api';
import React, { useEffect, useState } from 'react';
import ReactLoading from 'react-loading';
import { useNavigate } from 'react-router-dom';
import { Schema } from '../../../../amplify/data/resource';
import { EmptyState } from '../../../components/custom/EmptyState';
import { useAuth } from '../../../utils/context/useAuthContext';
import { useUtils } from '../../../utils/context/useUtilsContext';
import FileList from './FileList/FileList';

const apiClient = generateClient<Schema>();

const selectionSet = [
  'id',
  'viewed',
  'createdAt',
  'path',
  'date',
  'status',
  'error',
  'type',
  'processed',
  'xmlVersion',
  'executionDate',
  'workEnvironment',
  'fileNumber',
  'sequenceNumber',
  'senderCode',
  'senderIdentifierType',
  'senderId',
  'senderName',
  'senderContactFirstName',
  'senderContactLastName',
  'senderContactPhone',
  'senderContactEmail',
  'senderContactCellular',
  'recipientCode',
  'recipientIdentifierType',
  'recipientId',
  'recipientCreatorIdentifier',
  'userId',
  'user.*',
  'deposits.totalDepositAmount',
  'deposits.employees.employeeId',
  'deposits.employees.salaryMonth',
] as const;

// @ts-ignore
type File = SelectionSet<Schema['File']['type'], typeof selectionSet>;

const Dashboard: React.FC = (): JSX.Element => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);

  const [loading, setLoading] = useState(false);

  const { user } = useAuth();

  const userId = user?.userId;

  const { closeUploadDialog, openUploadDialog, isUploadFileDialogOpen } =
    useUtils();

  const toggleOpenUploadFile = () => {
    if (isUploadFileDialogOpen) {
      closeUploadDialog();
    } else {
      openUploadDialog();
    }
  };

  const [files, setFiles] = useState<File[]>([]);

  const fetchFiles = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data, errors } = await apiClient.models.File.list({
        filter: { userId: { eq: userId } },

        selectionSet: selectionSet,
      });
      if (errors) {
        console.error('Error fetching files:', errors);
      }

      setFiles(data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error during fetchUsers:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();

    if (!userId) return;

    const createSub = apiClient.models.File.onCreate({
      filter: { userId: { eq: userId } },
    }).subscribe({
      next: () => fetchFiles(),
      error: (error: string) => console.warn('onCreate error', error),
    });

    const updateSub = apiClient.models.File.onUpdate({
      filter: { userId: { eq: userId }, processed: { eq: true } },
    }).subscribe({
      next: () => fetchFiles(),
      error: (error: string) => console.warn('onUpdate error', error),
    });

    return () => {
      createSub.unsubscribe();
      updateSub.unsubscribe();
    };
  }, [userId]);

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        gap: '20px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '10xp',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <img
            alt="file"
            src={'/assets/header/file.svg'}
            onClick={toggleOpenUploadFile}
            style={{ cursor: 'pointer' }}
          />
        </Box>
        <Typography
          fontSize="30px"
          fontWeight="700"
          fontFamily={'AlmoniDL'}
          textAlign={'right'}
          sx={{ whiteSpace: 'nowrap' }}
        >
          רשימת הפקדות
        </Typography>
      </Box>
      <Divider
        sx={{
          width: '100%',
          height: '1px',
          backgroundColor: theme.palette.dark.border,
        }}
      />

      {loading ? (
        <Box sx={{ mt: '100px', display: 'flex', justifyContent: 'center' }}>
          <ReactLoading
            type={'bubbles'}
            color={theme.palette.green.main}
            height={250}
            width={250}
          />
        </Box>
      ) : files?.length ? (
        <FileList files={files} />
      ) : (
        <EmptyState />
      )}
    </Box>
  );
};

export default Dashboard;
